import { PerkSectionsData, Query } from '@varo/graphql-schema-types';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

interface UseRewardsPaginationProps {
  sectionsData?: Query;
  after: string;
  setAfter: Dispatch<SetStateAction<string>>;
  hasErrors?: boolean;
}

export const useRewardsPagination = ({
  sectionsData,
  after,
  setAfter,
  hasErrors,
}: UseRewardsPaginationProps) => {
  const [sections, setSections] = useState<PerkSectionsData[]>([]);

  const fetchingMoreRef = useRef(false);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    if (hasErrors) {
      fetchingMoreRef.current = false;
      isRefreshingRef.current = false;
    }
  }, [hasErrors]);

  useEffect(() => {
    if (!sectionsData) {
      return;
    }

    const edges = sectionsData.myPartnerOffers?.perkSections?.edges ?? [];
    const newSections = edges.map((edge) => edge.node);

    if (isRefreshingRef.current) {
      setSections(newSections);
      isRefreshingRef.current = false;
    } else {
      setSections((prev) => {
        const existingIds = new Set(prev.map((s) => s.id));
        return [...prev, ...newSections.filter((s) => !existingIds.has(s.id))];
      });
    }
    fetchingMoreRef.current = false;
  }, [sectionsData]);

  const loadMore = () => {
    const hasNextPage =
      sectionsData?.myPartnerOffers?.perkSections?.pageInfo?.hasNextPage;

    if (fetchingMoreRef.current || !hasNextPage) {
      return;
    }

    fetchingMoreRef.current = true;
    setAfter(
      sectionsData.myPartnerOffers.perkSections.pageInfo?.endCursor ?? '',
    );
  };

  const startRefresh = () => {
    isRefreshingRef.current = true;
    fetchingMoreRef.current = false;
    if (after !== '') {
      setAfter('');
    }
  };

  const clearSections = () => {
    setSections([]);
  };

  return {
    sections,
    fetchingMoreRef,
    isRefreshingRef,
    loadMore,
    startRefresh,
    clearSections,
  };
};
